import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const MedicineCartContext = createContext(null);
const STORAGE_KEY = 'medicine_cart_items';

function loadCartFromStorage() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function MedicineCartProvider({ children }) {
  const [items, setItems] = useState(loadCartFromStorage);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedPatientName, setSelectedPatientName] = useState('');

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // ignore quota errors
    }
  }, [items]);

  const addItem = useCallback((item) => {
    const skuId = String(item.skuId || item.id || '').trim();
    if (!skuId) return;
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.skuId === skuId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: (next[idx].quantity || 1) + (item.quantity || 1) };
        return next;
      }
      return [
        ...prev,
        {
          skuId,
          id: skuId,
          name: item.name || '',
          type: item.type || 'otc',
          price: item.price,
          quantity: item.quantity || 1,
          packSizeLabel: item.packSizeLabel || '',
          prescriptionRequired: !!item.prescriptionRequired,
        },
      ];
    });
  }, []);

  const updateQuantity = useCallback((skuId, quantity) => {
    const q = Math.max(1, Number(quantity) || 1);
    setItems((prev) => prev.map((i) => (i.skuId === skuId ? { ...i, quantity: q } : i)));
  }, []);

  const removeItem = useCallback((skuId) => {
    setItems((prev) => prev.filter((i) => i.skuId !== skuId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const value = {
    items,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    count: items.reduce((s, i) => s + (i.quantity || 1), 0),
    selectedAddress,
    setSelectedAddress,
    selectedPatientName,
    setSelectedPatientName,
  };

  return <MedicineCartContext.Provider value={value}>{children}</MedicineCartContext.Provider>;
}

export function useMedicineCart() {
  const ctx = useContext(MedicineCartContext);
  if (!ctx) throw new Error('useMedicineCart must be used within MedicineCartProvider');
  return ctx;
}
